import React, { useState, useRef } from 'react';

import SearchAndFilter from './SearchAndFilter';
import Row from './SearchUsersOrGroupsTableRow';
import useOnScreen from  '@/components/hooks/useOnScreen';

import {ActiveTabs} from  '@/components/modals/SelectUsersOrUserGroupsModal';

import { SearchType } from '@/context/LockerManagementFileUploadContext';
import { useEffect } from 'react';
import { container, TYPES } from  '@/inversifyconfig/container.config';
import { IHttpRequest } from  '@/interfaces/IHttp';
import { Users, Groups } from  '@/interfaces/LockerManagement';

interface  SearchUsersOrGroupsProps {
    activeTab: ActiveTabs
    addToSelected: (type: SearchType, data: any) => void 
    removeFromSelected: (type: SearchType, data: any) => void
    selectedUsers: any[]
    selectedGroups: any[]
    planner:boolean,
    filterParams?: Object
}

const SearchUsersOrGroups = ({ activeTab, addToSelected, removeFromSelected, selectedUsers, selectedGroups, planner, filterParams }: SearchUsersOrGroupsProps) => {

    /* Ref for infinite scrolling, if there are more files, load them and append to files when this comes into view */
    const scrollTo = useRef();
    const isVisible = useOnScreen(scrollTo, document.querySelector(`.search-results-container.${activeTab.split(' ').join('')}`));

    const [users, setUsers ] = useState<Users[]>([]);
    const [next, setNext] = useState<number>(0);
    const [total, setTotal] = useState<number>();

    const [groups, setGroups ] = useState<Groups[]>([]);
    const [searchTerms, setSearchTerms] = useState<{[key in ActiveTabs]: string}>({
        [ActiveTabs.GROUPS]: '',
        [ActiveTabs.SELECTEDGROUPS]: '',
        [ActiveTabs.SELECTEDUSERS]: '',
        [ActiveTabs.USERS]: ''
    });

    const [isLoading, setIsLoading] = useState<boolean>(true);

    const httpCall = container.get<IHttpRequest>(TYPES.IHttpRequest); 
    
    const groupsSearch = searchTerms[ActiveTabs.GROUPS];
    const usersSearch = searchTerms[ActiveTabs.USERS]

    useEffect(() => {
        setIsLoading(true)
        if(activeTab === ActiveTabs.GROUPS) {
            setIsLoading(true)
            httpCall.get(`${planner ? 'search/org/groups' : 'locker/admin/groups'}`, {params: getQueryParams()})
                .then(({data:{groups, nextSkip}}) => {
                    setData({groups, nextSkip})
                })
                .catch(() => {})
                .finally(() => setIsLoading(false));
            } 
        else if(activeTab === ActiveTabs.USERS) {
            setIsLoading(true)
            httpCall.get(`${planner? `search/org/users` : 'locker/admin/users'}`, {params: getQueryParams()})
                .then(({data:{users, nextSkip, totalUserCount}}) => {
                    setData({users, nextSkip, total: totalUserCount})
            })
            .catch(() => { })
            .finally(() => setIsLoading(false));
        }
    }, [activeTab, groupsSearch, usersSearch])

    useEffect(()=>{
        isVisible && !isLoading && (getActiveList().source.length === next ) && nextPage();
    },[isVisible, total, isLoading])

    function setData({users=[], nextSkip, total=0, groups=[]}) {
        setUsers(users)
        setGroups(groups);
        setNext(nextSkip)
        setTotal(total)
    }

    function isChecked(data: Groups | Users, type: SearchType) {
        return type === SearchType.USER 
            ? selectedUsers.find(listItem => listItem.id === (data as Users).id)
            : selectedGroups.find(listItem => listItem.groupName === (data as Groups).groupName) 
    }

    function getQueryParams(skip = 0){
        let params = {
            SearchTerm: searchTerms[activeTab] ? searchTerms[activeTab] : undefined,
            Skip: skip
        };

        if(filterParams) {
            params = {...params, ...filterParams}
        }
        return params
    }

    function getPlaceholderText(){
        return {
            [ActiveTabs.GROUPS]: 'Search Groups',
            [ActiveTabs.SELECTEDGROUPS]: 'Search Selected Groups',
            [ActiveTabs.USERS]: 'Search Users',
            [ActiveTabs.SELECTEDUSERS]: 'Search Selected Users'
        }[activeTab]
    }

    function getActiveList(){
        return {
            [ActiveTabs.SELECTEDUSERS]: {source: selectedUsers, type: SearchType.USER, isSelected: true },
            [ActiveTabs.USERS]: {source: users, type: SearchType.USER, setList: setUsers, url: `${planner? 'search/org/users' : 'locker/admin/users'}`},
            [ActiveTabs.SELECTEDGROUPS]: {source: selectedGroups, type: SearchType.GROUP, isSelected: true},
            [ActiveTabs.GROUPS]: {source: groups, type: SearchType.GROUP, setList: setGroups, url: `${planner ? 'search/org/groups' : 'locker/admin/groups'}`}
        }[activeTab]
    }

    function nextPage(){
        const { setList, type, source, url } = getActiveList();

        function setNextPage(data){
            let list: any, next: number, total: number
            if (type === SearchType.USER) {
                list = data.users;
                next = data.nextSkip;
                total = data.totalUserCount;
            }
            else {
                list = data.groups;
                next = data.nextSkip;
                total = data.totalGroups;
            }
            setList([...source, ...list])
            setNext(next)
            setTotal(total)
        }

        setIsLoading(true)
        httpCall.get(url, {params: getQueryParams(next)})
           .then(({data}) => {
                setNextPage(data)
            })
            .catch((err) => {
               console.error(err.data)
            })
            .finally(() => setIsLoading(false));
    }
   
    function GetList({searchTerm}:{searchTerm: string}): JSX.Element {
        const list = getActiveList()
        
        // filter function to search locally stored selected lists and lists recieved through the api
        const filterList = (el: any) => {
            switch (activeTab) {
                case ActiveTabs.SELECTEDGROUPS: 
                    return el.groupName.toLowerCase().includes(searchTerm.toLowerCase())
                case ActiveTabs.SELECTEDUSERS:
                    return el.name.toLowerCase().includes(searchTerm.toLowerCase())
                default:
                    return el;
            } 
        } 
           
        const filteredList = list.source.filter(filterList);
        return (
            <>{filteredList.length
            ? <ul>
                {filteredList.map((i, idx) =>
                    <Row
                        key={`${list.type}-${idx + 1}`}
                        data={i}
                        type={list.type}
                        isSelected={list.isSelected}
                        addToSelected={addToSelected}
                        removeFromSelected={removeFromSelected}
                        isChecked={isChecked(i, list.type)} 
                    />
                )}
            </ul>
            :<div>
                <h4 className="not-found">{ [ActiveTabs.SELECTEDUSERS, ActiveTabs.SELECTEDGROUPS].includes(activeTab)
                    ? <>No {activeTab.split(' ')[1]} Selected </>
                    : <>No results found for "{searchTerm}"</>
                }</h4>
            </div>
           
            }</>
        )
    }

    return (
        <>
            <SearchAndFilter
                key={activeTab}
                placeholder={getPlaceholderText()}
                hasDropDown={false}
                onSearchSubmit={({searchTerm}) => setSearchTerms({...searchTerms, [activeTab]:searchTerm})}
                className='modal-search'
                prevValues={{searchTerm: searchTerms[activeTab]}}
                isLoading={isLoading}
            />
            <div className={`search-results-container narrow-scroll-bar ${activeTab.split(' ').join('')}`}>
                <GetList searchTerm={searchTerms[activeTab]} />
                {[ActiveTabs.GROUPS, ActiveTabs.USERS].includes(activeTab) && 
                    <div key={activeTab} className={`next-page-wrapper${isLoading ? ' loading':''}`} ref={scrollTo} />
                }
            </div>
        </>
    
    )
}

export default SearchUsersOrGroups